/**
 * Playground API
 * Image generation using /v1/images endpoints
 */

import { baseApiClient } from './client';
import type {
  GenerateImageRequest,
  GenerateImageResponse,
  ImageTask,
  LibraryItem,
  PromptGenerationRequest,
  PromptGenerationResponse,
  PromptAssistTemplateResponse,
  ImageModelInfo,
  LuckyCharmRequest,
  LuckyCharmResponse,
} from '@/types/playground';

export const playgroundApi = {
  // Model Discovery API (Public - no admin auth required)
  models: {
    /**
     * List available image models with their capabilities
     * @param operation - Filter by operation type: 'generate' | 'edit' | 'fuse'
     */
    list: async (operation?: 'generate' | 'edit' | 'fuse'): Promise<{
      models: ImageModelInfo[];
      count: number;
      default_model: string | null;
    }> => {
      const params = operation ? { operation } : {};
      const response = await baseApiClient.get('/v1/images/models', { params });
      return response.data;
    },

    /**
     * Get a specific model by ID
     */
    get: async (modelId: string): Promise<ImageModelInfo> => {
      const response = await baseApiClient.get(`/v1/images/models/${modelId}`);
      return response.data;
    },

    /**
     * Get recommended model for an operation
     */
    recommend: async (params: {
      operation: 'generate' | 'edit' | 'fuse';
      prefer_fast?: boolean;
      fuse_image_count?: number;
    }): Promise<{ model_id: string; reason: string; params: Record<string, unknown> }> => {
      const response = await baseApiClient.get('/v1/images/models/recommend', { params });
      return response.data;
    },
  },

  // Image Generation API (Primary)
  images: {
    /**
     * Generate image with full configuration
     */
    generate: async (data: GenerateImageRequest): Promise<GenerateImageResponse> => {
      const response = await baseApiClient.post('/v1/images/generate', data);
      return response.data;
    },

    /**
     * Edit existing image
     */
    edit: async (data: GenerateImageRequest): Promise<GenerateImageResponse> => {
      const response = await baseApiClient.post('/v1/images/edit', data);
      return response.data;
    },

    /**
     * Fuse multiple images
     */
    fuse: async (data: GenerateImageRequest): Promise<GenerateImageResponse> => {
      const response = await baseApiClient.post('/v1/images/fuse', data);
      return response.data;
    },

    /**
     * Generate Lucky Charm image from a provided phrase.
     * Phrase generation (Agent/LLM) happens in the caller; this endpoint
     * is composition-only.
     */
    luckyCharm: async (data: LuckyCharmRequest): Promise<LuckyCharmResponse> => {
      console.log('[PlaygroundAPI] Generating Lucky Charm image, phrase:', data.phrase?.substring(0, 30) + '...');
      const response = await baseApiClient.post('/v1/images/lucky-charm', data);
      return response.data;
    },

    /**
     * Get available Lucky Charm themes/packs
     */
    getLuckyCharmThemes: async (): Promise<{
      themes: Array<{
        id: string;
        display_name: string;
        description: string;
        image_count: number;
        is_root?: boolean;
        has_overlay?: boolean;
        font_color?: string;
        rotation_angle?: number;
      }>;
      count: number;
    }> => {
      const response = await baseApiClient.get('/v1/agents/dora/skills/lucky-charm/themes');
      return response.data;
    },

    /**
     * Get available Lucky Charm background images
     * @param themeId - Optional theme ID to filter images
     */
    getLuckyCharmImages: async (themeId?: string): Promise<{
      images: string[];
      image_list: Array<{
        id: string;
        filename: string;
        display_name: string;
        theme?: string;
        theme_id?: string;
        emotion?: string;
        has_metadata: boolean;
      }>;
      count: number;
      theme_id?: string;
    }> => {
      const params = themeId ? { theme_id: themeId } : {};
      const response = await baseApiClient.get('/v1/agents/dora/skills/lucky-charm/available-images', { params });
      return response.data;
    },

    /**
     * Get available Lucky Charm fonts
     */
    getLuckyCharmFonts: async (): Promise<{
      fonts: Array<{ id: number; name: string; path: string; display_name?: string; description?: string; is_default?: boolean }>;
      default_font_id: number | null;
      count: number;
    }> => {
      const response = await baseApiClient.get('/v1/agents/dora/skills/lucky-charm/fonts');
      return response.data;
    },

    /**
     * Get task by ID
     */
    getTask: async (taskId: string): Promise<ImageTask> => {
      const response = await baseApiClient.get(`/v1/images/tasks/${taskId}`);
      const task = response.data;
      return {
        ...task,
        id: task.task_id,
        type: task.task_type,
        config: { model: task.model },
        result: task.temp_url
          ? {
              image_url: task.temp_url,
              video_url: task.video_url,
              video_object_name: task.video_object_name,
            }
          : undefined,
      };
    },

    /**
     * List all tasks (history)
     */
    listTasks: async (params?: {
      type?: string;
      status?: string;
      limit?: number;
      offset?: number;
    }): Promise<{ tasks: ImageTask[]; total: number }> => {
      const response = await baseApiClient.get('/v1/images/tasks', { params });
      // Backend returns array directly, wrap it and add convenience aliases
      const rawTasks = Array.isArray(response.data) ? response.data : [];
      const tasks = rawTasks.map((task: ImageTask) => ({
        ...task,
        // Add convenience aliases for backward compatibility
        id: task.task_id,
        type: task.task_type,
        config: { model: task.model },
        result: task.temp_url
          ? {
              image_url: task.temp_url,
              video_url: task.video_url,
              video_object_name: task.video_object_name,
            }
          : undefined,
      }));
      return {
        tasks,
        total: tasks.length,
      };
    },

    /**
     * Delete a task
     */
    deleteTask: async (taskId: string): Promise<void> => {
      await baseApiClient.delete(`/v1/images/tasks/${taskId}`);
    },

    /**
     * Clear all tasks
     */
    clearTasks: async (): Promise<void> => {
      await baseApiClient.delete('/v1/images/tasks');
    },

    /**
     * Save task result to permanent library storage
     * This moves the temp file to library - no re-download needed!
     */
    saveTask: async (taskId: string, options?: {
      category?: string;
      filename?: string;
      metadata?: Record<string, unknown>;
    }): Promise<{ file_id: string; permanent_url: string }> => {
      const response = await baseApiClient.post(`/v1/images/tasks/${taskId}/save`, options || {});
      return response.data;
    },
  },

  // Image Library
  library: {
    /**
     * List saved images
     * Uses new unified resources API: GET /v1/resources/library
     */
    list: async (params?: {
      category?: string;
      limit?: number;
      offset?: number;
    }): Promise<{ items: LibraryItem[]; total: number }> => {
      const response = await baseApiClient.get('/v1/resources/library', {
        params: {
          category: params?.category,
          limit: params?.limit,
          include_urls: true,
        },
      });
      // New API returns { resources: [...], total: N, has_more: bool }
      // Map to old format for backward compatibility with UI
      const resources = response.data.resources || [];
      return {
        items: resources.map((r: { id: string; name: string; content_type: string; size: number; created_at: string; url?: string; subtype?: string; metadata?: Record<string, unknown> }) => ({
          id: r.id.replace('library:', ''),
          filename: r.name,
          content_type: r.content_type,
          size: r.size,
          created_at: r.created_at,
          url: r.url,
          category: r.subtype,
          metadata: r.metadata,
        })),
        total: response.data.total || 0,
      };
    },

    /**
     * Save image to library
     * Uses new unified resources API: POST /v1/resources/library
     */
    save: async (data: {
      image_url?: string;
      image_base64?: string;
      filename?: string;
      category?: string;
      metadata?: Record<string, unknown>;
    }): Promise<LibraryItem> => {
      const response = await baseApiClient.post('/v1/resources/library', data);
      // Map response to LibraryItem format
      return {
        id: response.data.file_id,
        filename: response.data.filename,
        content_type: response.data.content_type,
        size: response.data.size,
        created_at: response.data.created_at,
        url: response.data.url,
        category: data.category,
        metadata: data.metadata,
      };
    },

    /**
     * Get library item
     * Uses new unified resources API: GET /v1/resources/library/{file_id}
     */
    get: async (itemId: string): Promise<LibraryItem> => {
      const response = await baseApiClient.get(`/v1/resources/library/${itemId}`);
      const r = response.data;
      return {
        id: r.id.replace('library:', ''),
        filename: r.name,
        content_type: r.content_type,
        size: r.size,
        created_at: r.created_at,
        url: r.url,
        category: r.subtype,
        metadata: r.metadata,
      };
    },

    /**
     * Delete from library
     * Uses new unified resources API: DELETE /v1/resources/library/{file_id}
     */
    delete: async (itemId: string): Promise<void> => {
      await baseApiClient.delete(`/v1/resources/library/${itemId}`);
    },
  },

  // Prompt Generation (supports Agent Mode and Raw LLM Mode)
  promptAssist: {
    /**
     * Generate optimized prompt using AI
     *
     * Two modes available:
     * 1. Agent Mode (specify agentType): Uses Agent's personality
     * 2. Raw LLM Mode (specify model): Direct OpenAI API with custom system prompt
     *
     * @param data.user_input - User's description/guidance
     * @param data.operationMode - 'generate' | 'edit' | 'fuse'
     * @param data.agentType - Agent to use (Agent Mode)
     * @param data.model - LLM model ID (Raw Mode, e.g., 'gpt-4o-mini')
     * @param data.temperature - Sampling temperature (Raw Mode only)
     * @param data.system_prompt - Custom system prompt (Raw Mode only)
     * @param data.style_hints - Optional style hints
     * @param data.language - Output language preference
     */
    generate: async (data: PromptGenerationRequest): Promise<PromptGenerationResponse> => {
      // Determine mode for logging
      const mode = data.model ? 'Raw' : 'Agent';
      console.log(`[PlaygroundAPI] Generating prompt (${mode} Mode) via /v1/images/prompt-assist`);

      // Build request payload
      const payload: Record<string, unknown> = {
        user_input: data.user_input,
        operation: data.operationMode || data.operation || 'generate',
        style_hints: data.style_hints,
        context: data.context,
        language: data.language || 'zh',
        include_negative_prompt: data.include_negative_prompt,
        max_length: data.max_length,
      };

      // Agent Mode fields
      if (data.agentType) {
        payload.agent_type = data.agentType;
        // Pass output schema key for structured responses
        if (data.output_schema_key) {
          payload.output_schema_key = data.output_schema_key;
        }
      }

      // Raw LLM Mode fields
      if (data.model) {
        payload.model = data.model;
        if (data.temperature !== undefined) {
          payload.temperature = data.temperature;
        }
        if (data.system_prompt) {
          payload.system_prompt = data.system_prompt;
        }
      }

      const response = await baseApiClient.post('/v1/images/prompt-assist', payload);

      return {
        prompt: response.data.prompt,
        negative_prompt: response.data.negative_prompt,
        suggestions: response.data.suggestions || [],
        recommended_model: response.data.recommended_model,
        recommended_params: response.data.recommended_params,
        mode: response.data.mode,
        agent_used: response.data.agent_used,
        model_used: response.data.model_used,
        tokens_used: response.data.tokens_used,
        system_prompt: response.data.system_prompt,
        user_prompt: response.data.user_prompt,
      };
    },

    /**
     * Get system prompt templates for transparency
     * Shows what prompts are used in Raw LLM mode
     */
    getTemplate: async (): Promise<PromptAssistTemplateResponse> => {
      const response = await baseApiClient.get('/v1/images/prompt-assist/template');
      return response.data;
    },
  },

  // File upload utilities
  utils: {
    /**
     * Convert file to base64
     */
    fileToBase64: (file: File): Promise<string> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          const result = reader.result as string;
          // Remove data URL prefix (e.g., "data:image/png;base64,")
          const base64 = result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = (error) => reject(error);
      });
    },

    /**
     * Download image from URL
     */
    downloadImage: async (url: string, filename?: string): Promise<void> => {
      try {
        const response = await fetch(url);
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = filename || `image-${Date.now()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(objectUrl);
      } catch (error) {
        console.error('Failed to download image:', error);
        // Fallback: open in new tab
        window.open(url, '_blank');
      }
    },

    /**
     * Copy text to clipboard
     */
    copyToClipboard: async (text: string): Promise<boolean> => {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch {
        // Fallback for older browsers
        const textarea = document.createElement('textarea');
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        const success = document.execCommand('copy');
        document.body.removeChild(textarea);
        return success;
      }
    },
  },
};
